import { useEffect, useState } from 'react';
import healthService, { NewClientPause, Sunset } from '../api/services/health';

interface ClientIntake {
  pause: NewClientPause;
  sunset: Sunset;
}

const INACTIVE: ClientIntake = {
  pause: { active: false, pauseStart: null },
  sunset: { active: false, start: null },
};

// Fetched once per page load and shared across all consumers of the hook.
let cached: ClientIntake | null = null;
let pendingFetch: Promise<ClientIntake> | null = null;

const fetchIntake = (): Promise<ClientIntake> => {
  if (cached) return Promise.resolve(cached);

  if (!pendingFetch) {
    pendingFetch = healthService
      .find()
      .then((health) => {
        cached = {
          pause: health.newClientPause ?? INACTIVE.pause,
          sunset: health.sunset ?? INACTIVE.sunset,
        };
        return cached;
      })
      .catch(() => {
        // Fail open: never degrade the experience because the health check failed
        cached = INACTIVE;
        return cached;
      });
  }

  return pendingFetch;
};

/** Test seam: forget the cached /health read so the next mount fetches again. */
export const resetNewClientPauseCache = () => {
  cached = null;
  pendingFetch = null;
};

/**
 * Reads the new-client pause and sunset state from the public /health
 * endpoint, so both can be toggled via API env vars with no frontend rebuild.
 * While sunset is on, `pauseActive` is also true (the backend fences new
 * accounts the same way); `sunsetActive` picks the wording.
 */
const useNewClientPause = () => {
  const [intake, setIntake] = useState<ClientIntake>(cached ?? INACTIVE);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let mounted = true;
    fetchIntake().then((result) => {
      if (mounted) {
        setIntake(result);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return {
    pauseActive: intake.pause.active,
    pauseStart: intake.pause.pauseStart,
    sunsetActive: intake.sunset.active,
    sunsetStart: intake.sunset.start,
    loading,
  };
};

export default useNewClientPause;
